import { BaSeLoadingPage } from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import { RoleModel } from './modules/main/models/role-model';

export const HOME_ROUTE = '/';

export const ENROLLMENT_ROUTE = '/inscricao';
export const ENROLLMENT_ALREADY_COMPLETED_ROUTE = '/inscricao-realizada';
export const ENROLLMENT_CLOSED_ROUTE = '/inscricao-encerrada';

export const SEND_DOCS_ROUTE = '/envio-documentacao';

export const LIST_OF_ENROLLMENTS_ROUTE = '/listagem-inscricoes';
export const ENROLLMENT_DETAILS_ROUTE = '/detalhe-inscricao/:year/:cpf';

export const LIST_OF_NATIONAL_MANAGERS_ROUTE = '/listagem-gestor-nacional';
export const NATIONAL_MANAGER_REGISTRATION_ROUTE =
  '/gestor-nacional/:year/:cpf';

export const LIST_OF_STATE_MANAGERS_ROUTE = '/listagem-gestor-estadual';
export const STATE_MANAGER_REGISTRATION_ROUTE = '/gestor-estadual/:year/:cpf';

export const LIST_OF_JUDGES_ROUTE = '/listagem-pessoa-jurada';
export const JUDGE_REGISTRATION_ROUTE = '/pessoa-jurada/:year/:cpf';

export const LIST_OF_RATING_ROUTE = '/listagem-inscricoes-avaliacao';
export const RATING_ROUTE = '/avaliacao-inscricao/:year/:cpf/:scope/:status';

export const LIST_OF_RANKING_ROUTE = '/listagem-inscricoes-classificacao';

const AppContainer = React.lazy(
  () => import('./modules/main/containers/app-container'),
);
const LoginPage = React.lazy(() => import('./modules/main/pages/login-page'));
const HomePage = React.lazy(() => import('./modules/main/pages/home-page'));
const ForbiddenPage = React.lazy(
  () => import('./modules/main/pages/forbidden-page'),
);
const EnrollmentPage = React.lazy(
  () => import('./modules/main/pages/enrollment-page'),
);
const EnrollmentAlreadyCompletePage = React.lazy(
  () => import('./modules/main/pages/enrollment-already-complete-page'),
);
const EnrollmentClosedPage = React.lazy(
  () => import('./modules/main/pages/enrollment-closed-page'),
);
const SendDocsPage = React.lazy(
  () => import('./modules/main/pages/send-docs-page'),
);
const ListOfEnrollmentsPage = React.lazy(
  () => import('./modules/main/pages/list-of-enrollments-page'),
);
const EnrollmentDetailsPage = React.lazy(
  () => import('./modules/main/pages/enrollment-details-page'),
);
const ListOfTeamMembersPage = React.lazy(
  () => import('./modules/main/pages/list-of-team-members-page'),
);
const TeamMemberRegistrationPage = React.lazy(
  () => import('./modules/main/pages/team-member-registration-page'),
);
const ListOfRating = React.lazy(
  () => import('./modules/main/pages/list-of-rating-page'),
);
const EnrollmentRatingPage = React.lazy(
  () => import('./modules/main/pages/enrollment-rating-page'),
);
const ListOfRankingPage = React.lazy(
  () => import('./modules/main/pages/list-of-ranking-page'),
);

const createRoute = (route: Sebrae.RouteConfig) => ({
  ...route,
  isExactPath: true,
  pageToRender: (
    <React.Suspense fallback={<BaSeLoadingPage />}>
      <AppContainer>{route.pageToRender}</AppContainer>
    </React.Suspense>
  ),
  orRedirectTo: route.orRedirectTo ? (
    <React.Suspense fallback={<BaSeLoadingPage />}>
      {route.orRedirectTo}
    </React.Suspense>
  ) : undefined,
});

const availableRoutes: Sebrae.RoutesConfig = [
  {
    routePath: HOME_ROUTE,
    pageToRender: <HomePage />,
  },
  {
    routePath: ENROLLMENT_ROUTE,
    ifIsAuthenticated: true,
    pageToRender: <EnrollmentPage />,
    orRedirectTo: <LoginPage />,
  },
  {
    routePath: ENROLLMENT_ALREADY_COMPLETED_ROUTE,
    ifIsAuthenticated: true,
    pageToRender: <EnrollmentAlreadyCompletePage />,
    orRedirectTo: <LoginPage />,
  },
  {
    routePath: ENROLLMENT_CLOSED_ROUTE,
    ifIsAuthenticated: true,
    pageToRender: <EnrollmentClosedPage />,
    orRedirectTo: <LoginPage />,
  },
  {
    routePath: SEND_DOCS_ROUTE,
    ifIsAuthenticated: true,
    pageToRender: <SendDocsPage />,
    orRedirectTo: <LoginPage />,
  },
  {
    routePath: LIST_OF_ENROLLMENTS_ROUTE,
    ifHaveTheseRoles: RoleModel.Manager,
    pageToRender: <ListOfEnrollmentsPage />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: ENROLLMENT_DETAILS_ROUTE,
    ifHaveTheseRoles: [...RoleModel.Manager, RoleModel.minervaJudge],
    pageToRender: <EnrollmentDetailsPage />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: LIST_OF_NATIONAL_MANAGERS_ROUTE,
    ifHaveTheseRoles: [RoleModel.nationalManager],
    pageToRender: <ListOfTeamMembersPage typeOfTeamMember="nationalManager" />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: NATIONAL_MANAGER_REGISTRATION_ROUTE,
    ifHaveTheseRoles: [RoleModel.nationalManager],
    pageToRender: (
      <TeamMemberRegistrationPage typeOfTeamMember="nationalManager" />
    ),
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: LIST_OF_STATE_MANAGERS_ROUTE,
    ifHaveTheseRoles: [RoleModel.nationalManager],
    pageToRender: <ListOfTeamMembersPage typeOfTeamMember="stateManager" />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: STATE_MANAGER_REGISTRATION_ROUTE,
    ifHaveTheseRoles: [RoleModel.nationalManager],
    pageToRender: (
      <TeamMemberRegistrationPage typeOfTeamMember="stateManager" />
    ),
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: LIST_OF_JUDGES_ROUTE,
    ifHaveTheseRoles: RoleModel.Manager,
    pageToRender: <ListOfTeamMembersPage typeOfTeamMember="judge" />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: JUDGE_REGISTRATION_ROUTE,
    ifHaveTheseRoles: RoleModel.Manager,
    pageToRender: <TeamMemberRegistrationPage typeOfTeamMember="judge" />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: LIST_OF_RATING_ROUTE,
    ifHaveTheseRoles: RoleModel.Any,
    pageToRender: <ListOfRating />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: RATING_ROUTE,
    ifHaveTheseRoles: [RoleModel.judge],
    pageToRender: <EnrollmentRatingPage />,
    orRedirectTo: <ForbiddenPage />,
  },
  {
    routePath: LIST_OF_RANKING_ROUTE,
    ifHaveTheseRoles: [...RoleModel.Manager, RoleModel.minervaJudge],
    pageToRender: <ListOfRankingPage />,
    orRedirectTo: <ForbiddenPage />,
  },
];

export const Routes = () => (
  <Sebrae.Routes routes={availableRoutes.map(createRoute)} />
);
